import type { Moment } from 'moment'
import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import type { DetailItemCardVariant } from './types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FullArrow from 'components/utils/Arrows/FullArrow'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import OutsideTripDatesIcon from 'tripPlanner/components/Itinerary/OutsideTripDatesIcon'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { CarRentalItem, FlightItem, TransportItem } from 'tripPlanner/types/tripItem'
import { calculateTravelTimeLabel } from 'tripPlanner/utils'

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(min-content, max-content) 1fr minmax(
      min-content,
      max-content
    );
  grid-gap: ${rem(16)};
  justify-content: space-between;
  align-items: center;
`

const Container = styled.div`
  position: relative;
  & *:empty {
    display: none;
  }
`

const OutsideTripDatesIconContainer = styled.div`
  *:empty {
    display: block;
  }
  position: absolute;
  left: calc(100% + ${rem(8)});
  top: ${rem(-2)};
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: row;
`

function formatDayOrDate(day: number | undefined, date: Moment | undefined) {
  if (day) {
    return `Day ${day}`
  } else if (date) {
    return date.format(DMY_CASUAL_SHORT_FORMAT)
  }
}

interface Props {
  item: FlightItem | TransportItem | CarRentalItem
  originLabel?: string
  destinationLabel?: string
  durationStyle?: 'exact' | 'days'
  variant: DetailItemCardVariant
}

function TransportTimeline({
  item,
  originLabel,
  destinationLabel,
  durationStyle = 'exact',
  variant,
}: Props) {
  const intl = useIntl()
  const timeFormat = useRegionTimeFormat()
  const {
    startDay,
    startDate,
    startTime,
    endDay,
    endDate,
    endTime,
    startUtcDateTime,
    endUtcDateTime,
  } = item

  const shouldCalculateTravelTime = startUtcDateTime && endUtcDateTime

  let duration: string | undefined = undefined
  if (shouldCalculateTravelTime) {
    const daysDifference = endUtcDateTime.diff(startUtcDateTime, 'days', true)
    if (durationStyle === 'days' && daysDifference >= 1) {
      const daysRoundedUp = Math.ceil(daysDifference)
      duration = intl.formatMessage({
        id: 'apVK0IG9NVKmo8s3TTEy',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Transport travel duration in days in the trip planner timeline view',
      }, { count: daysRoundedUp })
    } else {
      duration = calculateTravelTimeLabel(startUtcDateTime, endUtcDateTime)
    }
  }

  const openModal = useOpenModal()

  const editItem = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  return (
    <Layout>
      <Container>
        <DateContainer>
          <BodyText variant="small" colour="neutral-three">
            {formatDayOrDate(startDay, startDate)}
          </BodyText>
        </DateContainer>
        {variant !== 'drawer' && (
          <OutsideTripDatesIconContainer>
            <OutsideTripDatesIcon changeItemDates={editItem} item={item} />
          </OutsideTripDatesIconContainer>
        )}
        <Heading variant="heading6">{startTime?.format(timeFormat)}</Heading>
        <BodyText variant="small" colour="neutral-two">
          {originLabel}
        </BodyText>
      </Container>

      <VerticalSpacer gap={8}>
        <Caption align="center" variant="large" colour="neutral-three">
          {duration}
        </Caption>
        <FullArrow />
        <Caption align="center" variant="large">
          {duration ? <>&nbsp;</> : undefined}
        </Caption>
      </VerticalSpacer>

      <Container>
        <DateContainer>
          <BodyText variant="small" colour="neutral-three">
            {formatDayOrDate(endDay, endDate)}
          </BodyText>
        </DateContainer>
        <Heading variant="heading6">{endTime?.format(timeFormat)}</Heading>
        <BodyText variant="small" colour="neutral-two">
          {destinationLabel}
        </BodyText>
      </Container>
    </Layout>
  )
}

export default TransportTimeline
